import React, { useState } from 'react';
import { Alert, AlertColor, Avatar, Box, Button, Container, Input, Link, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import {
  authContainerStyling,
  authFormHeaderStyling,
  authFormPrimaryContainerStyling,
  authFormSecondaryContainerStyling,
  authHeaderContainerStyling,
  authHeaderTypographyStyling,
  authLogoStyling,
  authSecondaryContainerStyling,
  externalFormStyling,
} from '../../styles/sxStyling';
import { HelpOutline, LockOutlined } from '@mui/icons-material';
import { useGetEventFormTemplateById } from '../../services/react-query/event_form_templates/useGetEventFormTemplateById';
import { useNavigate, useParams } from 'react-router-dom';
import { Loading } from '../../uikit';
import { useCreateFormSubmission } from '../../services/react-query/form_submissions';
import { AxiosError, AxiosResponse } from 'axios';
import { useGetFormSubmissionById } from '../../services/react-query/form_submissions/useGetFormSubmissionById';
import CustomAxios from '../../utility/customAxios';
import { ROUTE_PATHS } from '../../routes/Routes';

type Response = {
  severity: AlertColor;
  message: string;
};

export default function FormAuthorizePassword() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [response, setResponse] = useState<Response | null>(null);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const { data: eventFormSubmission } = useGetEventFormTemplateById({ submission_uuid: uuid });

  if (!eventFormSubmission) return <Loading />;

  const handleSubmit = async () => {
    try {
      await CustomAxios.post(`form_submissions/${uuid}/authorize`, { verification_code: verificationCode });
      navigate(`/form/submit/${uuid}`);
    } catch (err: any) {
      setResponse({ severity: 'error', message: err?.response?.data?.error });
    }
  };

  // TODO check open/close dates and don't show form if outside

  return (
    <Container component="main" maxWidth="md" sx={authContainerStyling}>
      <title>ScheduLink | Studio Management Software for Volume Photo Studios</title>
      <Box sx={authSecondaryContainerStyling}>
        <Box sx={authHeaderContainerStyling}>
          <Typography variant="h1" sx={authHeaderTypographyStyling}>
            Securely send data for your {eventFormSubmission.event_name} to {eventFormSubmission.organization_name}
          </Typography>
          <Box component="img" src="/svg/schedulink-logo.svg" alt="logo" sx={authLogoStyling} />
        </Box>
        <Box sx={authFormPrimaryContainerStyling}>
          <Box sx={authFormSecondaryContainerStyling}>
            <Box sx={authFormHeaderStyling}>
              <Avatar sx={{ m: 1, bgcolor: 'primary.light' }}>
                <LockOutlined />
              </Avatar>
              <Typography variant="h5">Log In</Typography>
            </Box>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={externalFormStyling}>
              <Box sx={{ display: 'flex' }}>
                <TextField required autoFocus label="Verification Code" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} />
              </Box>
              <Box>
                <Button fullWidth variant="contained" onClick={handleSubmit} sx={{ mt: 3, mb: 2 }}>
                  Continue
                </Button>
                <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
                  By logging in, you agree to ScheduLink&apos;s{' '}
                  <Link href="https://schedulink.io/terms-of-service" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="https://schedulink.io/privacy-policy" target="_blank">
                    Privacy Statement
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Snackbar open={!!response} onClose={() => setResponse(null)}>
        <Alert severity={response?.severity} sx={{ width: '100%' }}>
          {response?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
